import { createContext, useContext, useState, useEffect } from 'react';
import api from '../../services/api/apiClient';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeJwt(token: string | null) {
    if (!token) return null;
    try {
        const payload = token.split('.')[1];
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const queryClient = useQueryClient();

    const [user, setUser] = useState<UserDto | null>(null);
    const [permissions, setPermissions] = useState<ResourcePermissions>(() => {
        try {
            return JSON.parse(sessionStorage.getItem('permissions') || '{}');
        } catch {
            return {};
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async function init() {
            try {
                const token = sessionStorage.getItem('token');
                const payload = decodeJwt(token);
                const exp = payload?.exp ? payload.exp * 1000 : null;

                if (!token) {
                    // try silent refresh (refresh-token cookie)
                    await attemptRefresh();
                } else if (exp && exp <= Date.now()) {
                    // token expired -> refresh
                    await attemptRefresh();
                } else {
                    // token valid -> call /api/auth/me to get authoritative user & permissions
                    await fetchProfile();
                }
            } catch (err) {
                // ignore, will logout below
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const fetchProfile = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;
            const res = await api.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });

            const userDto: UserDto = res.data.user;
            const perms: ResourcePermissions = res.data.permissions || {};

            setUser(userDto);
            setPermissions(perms);

            sessionStorage.setItem('permissions', JSON.stringify(perms));
        } catch {
            // failed to fetch profile -> try refresh
            await attemptRefresh();
        }
    };

    const attemptRefresh = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;

            // use absolute url + withCredentials to send cookie
            const refreshUrl = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/api/auth/refresh-token`;
            const refreshRes = await axios.post(refreshUrl, {}, { withCredentials: true });
            const newToken = (refreshRes.data.token || refreshRes.data.Token) as string | undefined;
            if (newToken) {
                sessionStorage.setItem('token', newToken);
                // after refresh, request profile
                await fetchProfile();
            } else {
                logout(); // can't refresh
            }
        } catch {
            logout();
        }
    };

    const login = async (credentials: Credentials) => {
        try {
            const res = await api.post('/api/auth/login', credentials, { headers: { 'Content-Type': 'application/json' }, withCredentials: true });

            const token = res.data.token || res.data.Token;
            if (!token) return { success: false, message: res.data.message || 'Aucun token reçu' };
            sessionStorage.setItem('token', token);

            const perms = res.data.permissions || {};
            sessionStorage.setItem('permissions', JSON.stringify(perms));
            setPermissions(perms);

            setUser(res.data.user);

            return { success: true, message: null };
        } catch (err: any) {
            const message = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Erreur login';
            return { success: false, message };
        }
    };

    const logout = async () => {
        queryClient.clear();

        // ---

        const token = sessionStorage.getItem('token');

        sessionStorage.removeItem('token');
        sessionStorage.removeItem('permissions');

        setUser(null);
        setPermissions({});

        if (token) {
            try {
                await api.post('/api/auth/logout', null, {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true,
                });
            } catch {}
        }
    };

    return <AuthContext.Provider value={{ user, permissions, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
};
